/**
 * Чтение свежего значения из ряда самочувствия — 2026-08-03.
 *
 * ═══ ПОЧЕМУ ЭТОТ МОДУЛЬ ПОЯВИЛСЯ ═══
 *
 * `/athlete/wellness` отдаёт дни ПО ВОЗРАСТАНИЮ и добивает пропуски пустыми
 * строками, чтобы окно было сплошным. Значит `rows[0]` — самый СТАРЫЙ день
 * окна, а не сегодняшний.
 *
 * «Сегодня» брал ровно `wellness[0]` и печатал под кольцом готовности HRV и
 * сон четырнадцатидневной давности как текущие. Незаметно это было потому,
 * что величина медленная: на замере по 107 окнам медиана расхождения со
 * свежим значением — 5.4 пункта HRV, и старое число всегда попадало в
 * правдоподобный коридор. В половине окон ошибка превышала 5 пунктов, а
 * дельта «выше/ниже обычного» считалась именно от неё.
 *
 * «Форма» в той же ситуации читала ряд с конца и показывала верно — отсюда
 * два разных HRV на двух экранах одного приложения.
 *
 * ═══ ПОЧЕМУ КАЖДАЯ МЕТРИКА ИЩЕТ СВОЙ ПОСЛЕДНИЙ ДЕНЬ ═══
 *
 * Метрики приходят в разном ритме: сон каждую ночь, вес — раз в несколько
 * дней. Общая «последняя полная строка» выбросила бы свежий сон ради того,
 * чтобы совпасть с давним взвешиванием. Поэтому поиск идёт по конкретному
 * ключу, и вместе со значением возвращается ЕГО дата — чтобы экран мог
 * сказать «измерено два дня назад», а не выдавать позавчерашнее за сегодня.
 */

/** Строка ряда: дата обязательна, остальные поля читаются по имени. */
export interface DatedRow {
  date: string;
}

export interface LatestValue {
  value: number | null;
  /** Дата ИМЕННО этого значения, не строки и не окна. */
  date: string | null;
}

/** Последнее известное значение метрики и день, когда оно измерено. */
export function latestMetric(
  rows: DatedRow[] | undefined,
  key: string,
): LatestValue {
  if (!rows) return { value: null, date: null };
  for (let i = rows.length - 1; i >= 0; i -= 1) {
    const v = (rows[i] as unknown as Record<string, number | null>)[key];
    if (typeof v === "number") return { value: v, date: rows[i].date };
  }
  return { value: null, date: null };
}

/**
 * Заполнена ли строка за конкретный день хотя бы одним из полей.
 *
 * Само НАЛИЧИЕ строки ничего не значит: окно сплошное, и день без данных
 * всё равно присутствует в нём пустой строкой. Проверять надо содержимое.
 */
export function hasEntryOn(
  rows: DatedRow[] | undefined,
  isoDate: string,
  keys: readonly string[],
): boolean {
  const row = rows?.find((r) => r.date === isoDate);
  if (!row) return false;
  const rec = row as unknown as Record<string, number | null | undefined>;
  return keys.some((k) => typeof rec[k] === "number");
}

/** Поля, которые пишет утренний чек-ин (WellnessCheckinPage). */
export const CHECKIN_FIELDS = [
  "readiness",
  "sleep_score",
  "sleep_hours",
  "hrv",
  "rhr",
  "body_weight",
] as const;
